import * as React from 'react';

import { useTranslations } from '@rematter/modules/transportation/i18n/useTranslations';
import { ComboBox, Button } from '@rematter/ui-web';
import { humanReadableAddress, cn } from '@rematter/ui-web/lib';
import {
  LucideBriefcaseBusiness,
  LucideMapPin,
  LucidePlus,
  LucideUser,
} from 'lucide-react';

import { useAccountAddressesQuery } from '../../hooks/queries/useAccountAddressesQuery';
import { useFacilitiesQuery } from '../../hooks/queries/useFacilitiesQuery';
import { ServiceFormState } from '../../types';

import useDebouncedValue from '~/shared/hooks/useDebounce';

export type NewLocationArgs = {
  type: 'facility' | 'account' | 'manual';
  accountId?: string;
  accountAddressId?: string;
  address?: ServiceFormState['address'];
};

type LocationOption = {
  id: string;
  name: string;
  address?: ServiceFormState['address'];
  type: 'facility' | 'account' | 'manual';
  accountType?: 'Commercial' | 'Individual';
  accountId?: string;
};

type Props = {
  facilityGroupId?: string;
  label: string;
  value?: string;
  isRequired?: boolean;
  isDisabled?: boolean;
  onAddNewLocation: () => void;
  onSelectLocation: (args: NewLocationArgs) => void;
  onRefetchLocations?: (refetch: () => void) => void;
};

type IconByOptionProps = {
  option: LocationOption;
  size?: number;
  className?: string;
};

const IconByOption = (props: IconByOptionProps) => {
  const { option, ...rest } = props;
  if (option.type === 'facility') {
    return <LucideMapPin {...rest} />;
  }
  if (option.type === 'account' && option.accountType === 'Individual') {
    return <LucideUser {...rest} />;
  }
  return <LucideBriefcaseBusiness {...rest} className="@IconByOption" />;
};

// The location select is a multi entity select that allows the user to select
// A facility, account, or enter their own address.
export const LocationSelect: React.FC<Props> = ({
  facilityGroupId,
  label,
  value,
  isRequired,
  isDisabled,
  onAddNewLocation,
  onSelectLocation,
  onRefetchLocations,
}) => {
  const t = useTranslations();
  const [searchTerm, setSearchTerm] = React.useState('');

  const debouncedSearchTerm = useDebouncedValue(searchTerm, 300);

  const {
    data: accountAddresses,
    isLoading: isAccountAddressesLoading,
    refetch: refetchAccountAddresses,
  } = useAccountAddressesQuery({
    searchTerm: debouncedSearchTerm,
    offset: 0,
    limit: 10,
  });

  const { data: facilities, isLoading: isFacilitiesLoading } =
    useFacilitiesQuery({
      searchTerm: debouncedSearchTerm,
      facilityGroupId,
      offset: 0,
      limit: 10,
    });

  const isLoading = isAccountAddressesLoading || isFacilitiesLoading;

  const facilityOptions = React.useMemo(
    () =>
      (facilities?.facilities ?? [])
        .map((facility) => ({
          id: facility.id,
          name: facility.facilityName,
          type: 'facility' as const,
          address: facility.address,
        }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [facilities],
  );

  const accountOptions = React.useMemo(
    () =>
      (accountAddresses?.addresses ?? [])
        .map((address) => ({
          id: address.id,
          name: [address.account.name, address.name]
            .filter(Boolean)
            .join(' - '),
          type: 'account' as const,
          address: address,
          accountId: address.account.id,
          accountType: address.account?.type as
            | 'Commercial'
            | 'Individual'
            | undefined,
        }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [accountAddresses],
  );

  // Flattened options for ComboBox with section headers
  const allLocationOptions = React.useMemo(() => {
    const options: (LocationOption & {
      isHeader?: boolean;
      section?: string;
    })[] = [];

    if (accountOptions.length > 0) {
      options.push(
        ...accountOptions.map((opt) => ({
          ...opt,
          section: 'Account',
          address: opt.address as ServiceFormState['address'],
        })),
      );
    }

    if (facilityOptions.length > 0) {
      options.push(
        ...facilityOptions.map((opt) => ({
          ...opt,
          section: 'Facility',
          address: opt.address as ServiceFormState['address'],
        })),
      );
    }

    return options;
  }, [facilityOptions, accountOptions]);

  // Memoized options for ComboBox
  const comboBoxOptions = React.useMemo(
    () =>
      allLocationOptions.map((option) => ({
        id: option.id,
        label: option.name,
        option, // Store the full option for rendering
      })),
    [allLocationOptions],
  );

  const handleSearch = React.useCallback((term: string) => {
    setSearchTerm(term);
  }, []);

  const handleSelectionChange = React.useCallback(
    (values: string[]) => {
      const selectedId = values?.[0];
      if (!selectedId) return;

      const selectedOption = allLocationOptions.find(
        (option) => option.id === selectedId,
      );

      if (selectedOption) {
        onSelectLocation({
          type: selectedOption.type,
          accountId: selectedOption.accountId,
          accountAddressId:
            selectedOption.type === 'account' ? selectedOption.id : undefined,
          address: selectedOption.address,
        });
      }
    },
    [allLocationOptions, onSelectLocation],
  );

  // Expose refetch through the provided callback
  React.useEffect(() => {
    if (onRefetchLocations) {
      onRefetchLocations(() => {
        refetchAccountAddresses();
      });
    }
  }, [onRefetchLocations, refetchAccountAddresses]);

  return (
    <div className="@LocationSelect space-y-2">
      <ComboBox
        label={label}
        options={comboBoxOptions}
        value={value ? [value] : []}
        onChange={handleSelectionChange}
        isRequired={isRequired}
        isDisabled={isDisabled}
        placeholder={t.servicesView.form.placeholders.location}
        query={searchTerm}
        onChangeQuery={handleSearch}
        isLoading={isLoading}
        renderOption={(option, state) => {
          const locationOption = option.option;
          const isFirstInSection =
            comboBoxOptions.findIndex(
              (opt) => opt.option.section === locationOption.section,
            ) === state.index;

          return (
            <div className="w-full">
              {isFirstInSection && locationOption.section && (
                <div className="border-b bg-neutral/5 px-3 py-1 text-center text-sm font-bold text-neutral/70">
                  {locationOption.section}
                </div>
              )}
              <div
                className={cn(
                  'flex flex-row justify-start gap-2 p-2',
                  state.focused && 'bg-primary text-white',
                )}
              >
                <IconByOption
                  option={locationOption}
                  size={14}
                  className={state.selected ? 'opacity-0' : ''}
                />
                <div>
                  <div
                    className={cn(
                      'text-sm font-bold',
                      state.focused && 'text-white',
                    )}
                  >
                    {locationOption.name}
                  </div>
                  <div
                    className={cn(
                      'text-xs',
                      state.focused ? 'text-white/75' : 'text-neutral/50',
                    )}
                  >
                    {locationOption.address?.streetAddress
                      ? humanReadableAddress(locationOption.address)
                      : 'N/A'}
                  </div>
                </div>
              </div>
            </div>
          );
        }}
        getSelection={(ids) => {
          const selectedOption = allLocationOptions.find(
            (opt) => opt.id === ids[0],
          );
          return selectedOption ? [{ label: selectedOption.name }] : [];
        }}
      />

      {/* Add New Location Button */}
      <div className="border-t border-neutral/20 pt-2">
        <Button
          onPress={onAddNewLocation}
          variant="link"
          isDisabled={isDisabled}
          icon={<LucidePlus size={14} />}
          className="flex w-full items-center justify-start gap-2 p-2 text-primary hover:text-primary-darker"
        >
          {t.servicesView.form.fields.newLocation}
        </Button>
      </div>
    </div>
  );
};
